import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect } from 'react';

import { useTypedParams } from '@sorare/routing';

import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { useFootballEvents } from '@sorare/marketplace/src/shared/events/lib';

import { LineupSharing } from '@sorare/gameplay/src/components/lineup/LineupSharing';
import { So5Lineup } from '@sorare/gameplay/src/components/lineup/So5Lineup';

import field from 'components/lineup/LineupLayout/assets/field.webp';

import type {
  FootballLineupSharingQuery,
  FootballLineupSharingQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const LINEUP_SHARING_QUERY = gql`
  query FootballLineupSharingQuery($id: ID!) {
    so5 {
      so5Lineup(id: $id) {
        id
        so5LeaderboardContender {
          slug
          ...So5Lineup_so5LeaderboardContender
        }
        ...LineupSharing_so5Lineup
      }
    }
  }
  ${LineupSharing.fragments.so5Lineup}
  ${So5Lineup.fragments.so5LeaderboardContender}
` as TypedDocumentNode<
  FootballLineupSharingQuery,
  FootballLineupSharingQueryVariables
>;

const FootballLineupSharing = () => {
  const track = useFootballEvents();
  const { id } = useTypedParams<PageParams>();
  const { data } = useQuery(LINEUP_SHARING_QUERY, {
    variables: {
      id,
    },
  });

  const { so5Lineup } = data?.so5 || {};
  const { so5LeaderboardContender } = so5Lineup || {};

  useEffect(() => {
    if (so5Lineup?.so5Leaderboard) {
      track('View Shared Lineup', {
        lineup: so5Lineup.id,
        competition: so5Lineup.so5Leaderboard.displayName,
      });
    }
  }, [so5Lineup, track]);

  if (!so5Lineup) return null;

  return (
    <LineupSharing
      so5Lineup={so5Lineup}
      so5LineupNode={
        !!so5LeaderboardContender && (
          <So5Lineup so5LeaderboardContender={so5LeaderboardContender} />
        )
      }
      field={field}
    />
  );
};

export default FootballLineupSharing;
